import React, { useState, useEffect, useCallback } from "react";
import { Settings, Shield, Wrench } from "lucide-react";
import { useSearchParams } from "react-router-dom";
import customFetch from "../../../helpers/axios";
import { normalizeApiResponse } from "../../../helpers/apiUtils";
import "./AccessToolsPermissions.css";
import Combobox from "../../components/Combobox/Combobox";
import UserStatCard from "../../components/UserStatCard/UserStatCard";
import SearchInput from "../../components/SearchInput/SearchInput";
import ToolPermissionModal from "./RoleToolPermissionModal/ToolPermissionModal";
import { useTranslation } from "react-i18next";
import useFormatDate from "../../../hooks/useFormatDate";
import useUser from "../../../hooks/useUser";
import Pagination from "../../components/Pagination/Pagination";
import "../../components/Pagination/Pagination.css";

export default function AccessToolsPermissions() {
  const { t } = useTranslation('permissions');
  const [permissions, setPermissions] = useState([]);
  const [allPermissions, setAllPermissions] = useState([]); // Para estadísticas totales
  const [integrations, setIntegrations] = useState([]);
  const [searchParams, setSearchParams] = useSearchParams();
  const [searchTerm, setSearchTerm] = useState(searchParams.get('search') || "");
  const [integrationFilter, setIntegrationFilter] = useState(searchParams.get('integration') || "all");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { user } = useUser();
  const [isRtpModalOpen, setIsRtpModalOpen] = useState(false);
  const [selectedPermission, setSelectedPermission] = useState(null);
  const [pagination, setPagination] = useState({ count: 0, next: null, previous: null });
  const [page, setPage] = useState(1);
  const [canonicalPageSize, setCanonicalPageSize] = useState(0);
  const [maxSeenPageSize, setMaxSeenPageSize] = useState(0);

  const fetchPermissions = useCallback(async (searchQuery = "", integrationQuery = "all", pageArg = 1) => {
    try {
      setLoading(true);
      
      // Construir parámetros de query
      const params = new URLSearchParams();
      params.set('page', String(pageArg));
      if (searchQuery && searchQuery.trim()) {
        params.append('search', searchQuery.trim());
      }
      if (integrationQuery && integrationQuery !== 'all') {
        // Filtrar por ID de integración
        params.append('integration', String(integrationQuery));
      }
      
      const queryString = params.toString();
      const url = `/api/auth/role-tool-permissions/${queryString ? `?${queryString}` : ''}`;
      
      const response = await customFetch.get(url, {
        headers: { Authorization: `Bearer ${user.access}` }
      });
      const normalized = normalizeApiResponse(response.data);
      const results = normalized.results || [];
      setPermissions(results);
      setPagination({
        count: normalized.count || 0,
        next: normalized.next || null,
        previous: normalized.previous || null,
      });

      const len = results.length;
      if (len > 0) {
        setCanonicalPageSize((prev) => {
          if (normalized.next && prev !== len) return len;
          if (!normalized.previous && prev === 0 && len > 0) return len;
          return prev;
        });
        setMaxSeenPageSize((prev) => (len > prev ? len : prev));
      }
    } catch (error) {
      console.error("Failed to fetch permissions:", error);
      setError(t('page.error'));
      setPermissions([]);
      setPagination({ count: 0, next: null, previous: null });
    } finally {
      setLoading(false);
    }
  }, [user.access, t]);

  const fetchAllPermissions = useCallback(async () => {
    try {
      const response = await customFetch.get("/api/auth/role-tool-permissions/", {
        headers: { Authorization: `Bearer ${user.access}` }
      });
      const normalized = normalizeApiResponse(response.data);
      setAllPermissions(normalized.results || []);
    } catch (error) {
      console.error("Failed to fetch all permissions:", error);
      setAllPermissions([]);
    }
  }, [user.access]);

  const fetchIntegrations = useCallback(async () => {
    try {
      const response = await customFetch.get("/api/integrations/integration/dropdown/", {
        headers: { Authorization: `Bearer ${user.access}` }
      });
      const data = Array.isArray(response.data) ? response.data : (normalizeApiResponse(response.data).results || []);
      setIntegrations(data);
    } catch (error) {
      console.error("Failed to fetch integrations:", error);
      setIntegrations([]);
    }
  }, [user.access]);

  useEffect(() => {
    if (user && user.access) {
      // Leer parámetros de URL al cargar
      const searchParam = searchParams.get('search') || '';
      const integrationParam = searchParams.get('integration') || 'all';
      
      // Sincronizar estado con URL
      setSearchTerm(searchParam);
      setIntegrationFilter(integrationParam);
      
      // Cargar datos con filtros de URL
      setPage(1);
      setCanonicalPageSize(0);
      setMaxSeenPageSize(0);
      fetchPermissions(searchParam, integrationParam, 1);
      
      // Cargar todos los permisos para estadísticas (sin filtros)
      fetchAllPermissions();
      
      fetchIntegrations();
    }
  }, [user, searchParams, fetchPermissions, fetchAllPermissions, fetchIntegrations]);

  // const handleDeletePermission = (permissionId) => {
  //   setPendingDeleteId(permissionId);
  //   setIsConfirmOpen(true);
  // };

  // const confirmDeletePermission = async () => {
  //   if (!pendingDeleteId) return;
  //   try {
  //     setDeleting(true);
  //     await customFetch.delete(`/api/auth/role-tool-permissions/${pendingDeleteId}/`, {
  //       headers: { Authorization: `Bearer ${user.access}` }
  //     });
  //     setPermissions(prev => prev.filter(p => p.id !== pendingDeleteId));
  //     setIsConfirmOpen(false);
  //     setPendingDeleteId(null);
  //   } catch (error) {
  //     console.error("Error deleting permission:", error);
  //   } finally {
  //     setDeleting(false);
  //   }
  // };

  const handleSearchChange = (value) => {
    setSearchTerm(value);
    const newParams = new URLSearchParams(searchParams);
    if (value) {
      newParams.set('search', value);
    } else {
      newParams.delete('search');
    }
    setSearchParams(newParams);
    
    // Llamar al backend con los nuevos filtros
    setPage(1);
    setCanonicalPageSize(0);
    setMaxSeenPageSize(0);
    fetchPermissions(value, integrationFilter, 1);
  };

  const handleIntegrationFilterChange = (value) => {
    setIntegrationFilter(value);
    const newParams = new URLSearchParams(searchParams);
    if (value && value !== 'all') {
      newParams.set('integration', value);
    } else {
      newParams.delete('integration');
    }
    setSearchParams(newParams);
    
    // Llamar al backend con los nuevos filtros
    setPage(1);
    setCanonicalPageSize(0);
    setMaxSeenPageSize(0);
    fetchPermissions(searchTerm, value, 1);
  };

  // Los datos ya vienen filtrados del backend, no necesitamos filtrar aquí
  const filteredPermissions = permissions;

  // Paginación del lado del servidor (similar a UserManagement)
  let startIndex = 0;
  let endIndex = 0;
  if (filteredPermissions.length > 0) {
    if (pagination.next) {
      startIndex = (page - 1) * filteredPermissions.length + 1;
    } else {
      startIndex = (pagination.count || 0) - filteredPermissions.length + 1;
    }
    endIndex = startIndex + filteredPermissions.length - 1;
  }
  const divisor = canonicalPageSize || maxSeenPageSize || (filteredPermissions.length || 1);
  const totalPages = Math.max(1, Math.ceil((pagination.count || 0) / divisor));

  // Generar opciones del dropdown de integración usando todas las integraciones disponibles
  const integrationOptions = [
    { value: "all", label: t('page.filterAllIntegrations') },
    ...integrations.map((integration) => ({
      value: String(integration.id ?? integration.value ?? integration.pk ?? integration.name),
      label: integration.display_name || integration.name || String(integration.id)
    }))
  ];
  const formatDate = useFormatDate();

  if (loading) {
    return (
      <div className="access-tools-root">
        <div className="flex justify-center items-center h-64">
          <div className="text-lg">{t('page.loading')}</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="access-tools-root">
        <div className="flex justify-center items-center h-64">
          <div className="text-lg text-red-600">{error}</div>
        </div>
      </div>
    );
  }

  return (
    <div className="access-tools-root">
      {/* Page Header */}
      <div className="access-tools-header">
        <div>
          <h1 className="access-tools-title">{t('page.title')}</h1>
          <p className="access-tools-subtitle">
            {t('page.subtitle')}
          </p>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="access-tools-stats grid grid-cols-1 md:grid-cols-3 gap-4">
        <UserStatCard
          label={t('page.stats.totalPermissions')}
          value={allPermissions.length}
          Icon={Shield}
          iconColor="#039bff"
        />
        <UserStatCard
          label={t('page.stats.integrations')}
          value={new Set(allPermissions.map(p => p.integration_name)).size}
          Icon={Settings}
          iconColor="#15803d"
        />
        <UserStatCard
          label={t('page.stats.totalTools')}
          value={allPermissions.reduce((sum, p) => sum + (p.tools_count || 0), 0)}
          Icon={Wrench}
          iconColor="#f59e42"
        />
      </div>

      {/* Filters and Search */}
      <div className="access-tools-table-section">
        <div className="access-tools-table-header">
          <div className="text-lg font-semibold">
            {t('page.sectionTitle')}
          </div>

          <div className="access-tools-searchbar">
            <SearchInput
              value={searchTerm}
              onChange={handleSearchChange}
              placeholder={t('page.searchPlaceholder')}
              className="flex-1"
            />

            <Combobox
              value={integrationFilter}
              onChange={handleIntegrationFilterChange}
              options={integrationOptions}
              buttonClass="access-tools-filter-select"
              label={t('page.filterAllIntegrations')}
            />
          </div>
        </div>

        {/* Permissions Table */}
        <div className="card">
          <div className="card-content p-0">
            <div className="overflow-x-auto">
              <table className="access-tools-table">
                <thead className="table-horizontal-lines">
                  <tr>
                    <th>{t('page.table.displayName')}</th>
                    <th>{t('page.table.name')}</th>
                    <th>{t('page.table.integration')}</th>
                    <th>{t('page.table.tools')}</th>
                    <th>{t('page.table.created')}</th>
                    {/* <th>{t('page.table.actions')}</th> */}
                  </tr>
                </thead>
                <tbody>
                  {filteredPermissions.map((permission) => (
                    <tr
                      key={permission.id}
                      className="table-horizontal-lines"
                      style={{ borderColor: "var(--muted)", cursor: "pointer" }}
                      onClick={() => {
                        setSelectedPermission(permission);
                        setIsRtpModalOpen(true);
                      }}
                    >
                      <td className="atp-cell-name">
                        <div className="flex items-center space-x-3">
                          <Shield size={16} className="text-blue-500" />
                          <span className="access-tools-permission-name">
                            {permission.display_name || permission.name || '-'}
                          </span>
                        </div>
                      </td>

                      <td className="atp-cell-display">
                        <span className="access-tools-display-name">
                          {permission.name}
                        </span>
                      </td>

                      <td className="atp-cell-integration">
                        <span className="badge filter-blue">
                          {permission.integration_name || '-'}
                        </span>
                      </td>

                      <td className="atp-cell-tools">
                        <div className="flex items-center space-x-2">
                          <Wrench size={14} className="text-gray-500" />
                          <span className="access-tools-tools-count">
                            {t('page.table.toolsCount', { count: permission.tools_count || 0 })}
                          </span>
                        </div>
                      </td>

                      <td className="atp-cell-date">
                        <span className="access-tools-date">
                          {formatDate(permission.created)}
                        </span>
                      </td>

                      {/* <td>
                        <div className="button-group-edit">
                          <button
                            onClick={() => {
                              setSelectedPermission(permission);
                              setIsRtpModalOpen(true);
                            }}
                            className="btn-edit"
                            title={t('page.actions.editTitle')}
                          >
                            <svg
                              className="icon-pencil"
                              xmlns="http://www.w3.org/2000/svg"
                              fill="none"
                              viewBox="0 0 24 24"
                              strokeWidth="1.5"
                              stroke="currentColor"
                              aria-hidden="true"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                d="M16.862 3.487a2.25 2.25 0 113.182 3.182L7.5 19.313 3 21l1.687-4.5L16.862 3.487z"
                              />
                            </svg>
                          </button>
                        </div>
                      </td> */}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {filteredPermissions.length > 0 && (
          <Pagination
            page={page}
            totalPages={totalPages}
            onPageChange={async (next) => {
              await fetchPermissions(searchTerm, integrationFilter, next);
              setPage(next);
            }}
            infoBelow={
              <span className="usage-page-info">
                {t('page.pagination.summary', {
                  start: filteredPermissions.length === 0 ? 0 : startIndex,
                  end: endIndex,
                  total: pagination.count || filteredPermissions.length,
                })}
              </span>
            }
          />
        )}
      </div>

      {filteredPermissions.length === 0 && (
        <div className="text-center py-8">
          <Shield size={48} className="mx-auto text-gray-400 mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            {t('page.emptyTitle')}
          </h3>
          <p className="text-gray-600">
            {t('page.emptyDesc')}
          </p>
        </div>
      )}

      {/* View Modal (Read-only) */}
      <ToolPermissionModal
        isOpen={isRtpModalOpen}
        onClose={() => setIsRtpModalOpen(false)}
        permission={selectedPermission}
        integrations={integrations}
        readOnly={true}
        onSaved={() => {
          // Refresh data after create/update
          fetchPermissions(searchTerm, integrationFilter);
          fetchAllPermissions();
          setSelectedPermission(null);
        }}
        // onDeleted={(deletedId) => {
        //   setPermissions((prev) => prev.filter((p) => p.id !== deletedId));
        //   fetchAllPermissions();
        //   setSelectedPermission(null);
        // }}
      />

      {/* Confirm Delete Modal
      <ConfirmModal
        isOpen={isConfirmOpen}
        title={t('page.confirm.deleteTitle')}
        message={t('page.confirm.deleteMessage')}
        confirmText={t('page.confirm.confirm')}
        cancelText={t('page.confirm.cancel')}
        onConfirm={confirmDeletePermission}
        onCancel={() => {
          if (deleting) return;
          setIsConfirmOpen(false);
          setPendingDeleteId(null);
        }}
        loading={deleting}
      /> */}
    </div>
  );
}
